import { useEffect, useState } from 'react';
import Modal from '../Modal';
import TextInput from '../TextInput';
import TextArea from '../TextArea';
import { useBusinessInfo } from '~/lib/Queries';

/**
 * Asks what to call a theme, who made it and how to describe it — either
 * before it is saved, or, given the theme, to change those later.
 *
 * What goes into a new theme is whatever the form looks like at the moment it
 * is saved. Editing only ever touches these details, never the look.
 */
const SaveThemeModal = ({
    isOpen,
    onClose,
    onSave,
    isSaving,
    defaultName = '',
    theme = null,
}) => {
    const { data: businessInfo } = useBusinessInfo();
    const businessName = businessInfo?.business?.name || '';

    const isEditing = !!theme;
    const initialName = isEditing ? theme.name || '' : defaultName;
    const initialAuthor = isEditing ? theme.author || '' : businessName;
    const initialDescription = isEditing ? theme.description || '' : '';

    const [name, setName] = useState(initialName);
    const [author, setAuthor] = useState(initialAuthor);
    const [description, setDescription] = useState(initialDescription);
    // Nothing is marked wrong until Save has been pressed, so a modal does not
    //  open already complaining about fields nobody has filled in yet
    const [hasTriedToSave, setHasTriedToSave] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setName(initialName);
            setAuthor(initialAuthor);
            setDescription(initialDescription);
            setHasTriedToSave(false);
        }
    }, [isOpen, initialName, initialAuthor, initialDescription]);

    // A description is optional — a theme is perfectly usable without one
    const nameError = name.trim() ? '' : 'Please enter a theme name';
    const authorError = author.trim() ? '' : 'Please enter an author';
    const canSave = !nameError && !authorError;

    function submit(ev) {
        ev.preventDefault();
        setHasTriedToSave(true);

        if (!canSave || isSaving) {
            return;
        }

        onSave({
            name: name.trim(),
            author: author.trim(),
            description: description.trim(),
        });
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} widthClass="max-w-md">
            <form onSubmit={submit} noValidate>
                {isEditing ? (
                    <h2 className="text-2xl font-semibold text-center mb-6">
                        Edit Theme
                    </h2>
                ) : (
                    <>
                        <h2 className="text-2xl font-semibold text-center mb-2">
                            Save as New Theme
                        </h2>
                        <div className="text-gray-500 text-center mb-6">
                            Use this look on your other forms. The colors,
                            fonts, input and button styles and custom CSS are
                            saved — the header and logo stay with this form.
                        </div>
                    </>
                )}

                <TextInput
                    id="theme-name"
                    value={name}
                    onChange={setName}
                    label="Theme Name"
                    placeholder="e.g. Spring 2026"
                    autoFocus={isOpen}
                    required={true}
                    error={hasTriedToSave ? nameError : ''}
                />

                <TextInput
                    id="theme-author"
                    value={author}
                    onChange={setAuthor}
                    label="Theme Author"
                    required={true}
                    className="mt-4"
                    error={hasTriedToSave ? authorError : ''}
                />

                <TextArea
                    id="theme-description"
                    value={description}
                    onChange={setDescription}
                    label="Theme Description (optional)"
                    placeholder="What this theme is for, e.g. our seasonal promotions"
                    className="mt-4"
                />

                <div className="flex items-center gap-3 mt-8">
                    <button
                        type="submit"
                        className="btn-save"
                        disabled={isSaving}
                    >
                        {isSaving
                            ? 'Saving…'
                            : isEditing
                              ? 'Save Changes'
                              : 'Save Theme'}
                    </button>
                    <button
                        type="button"
                        className="btn-gray-pill-lg"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default SaveThemeModal;
